import { all, call, put, select, takeLatest, takeEvery, fork, take } from 'redux-saga/effects';
import { gcApi } from 'config/axios';

// Utils
import NavigationUtil from 'utils/NavigationUtil';
import PrivacyAlertUtil from 'utils/PrivacyAlertUtil';
import { pushGroupFeedEventsToAnalytics, RESOURCE_TYPE, GROUP_ROLE, TAB_IDS } from 'utils/groups/GroupsUtil';
import { toQueryString } from 'utils/UrlUtil';
// utils -- userSocialProfileImage
import { PROFILE_IMAGE_VERSION } from 'utils/userSocialProfileImage/profileImageConstantsV2';

import { getActivityTypes } from 'reducers/activity/activityTypes';
import { fetchRouteDifficulties } from 'reducers/routeDifficulties/climbDifficulties';
import { fetchActivityTypes } from 'actions';
import { hideModal } from 'actions';
import {
    CONFIRM_DELETE_CONFIRM_DIALOG,
    CONFIRM_LEAVE_GROUP,
    DELETE_GROUP_ANNOUNCEMENT,
    DELETE_GROUP_POST,
    DELETE_GROUP_COMMENT,
    FETCH_GROUP,
    FETCH_GROUP_ALL_FEEDS,
    FETCH_GROUP_ALL_FEEDS_START,
    FETCH_GROUP_ALL_FEEDS_SUCCESS,
    FETCH_GROUP_ALL_FEEDS_FAILED,
    FETCH_GROUP_ACTIVITY_FEEDS,
    FETCH_GROUP_ACTIVITY_FEED,
    FETCH_GROUP_ACTIVITY_FEED_START,
    FETCH_GROUP_ACTIVITY_FEED_SUCCESS,
    FETCH_GROUP_ACTIVITY_FEED_FAILED,
    FETCH_GROUP_POSTS,
    FETCH_GROUP_POST,
    FETCH_GROUP_POST_START,
    FETCH_GROUP_POST_SUCCESS,
    FETCH_GROUP_POST_FAILED,
    FETCH_GROUP_COMMENTS,
    FETCH_GROUP_COMMENT_LIKERS,
    FETCH_GROUP_CONVERSATION_LIKERS,
    SAVE_GROUP_ANNOUNCEMENT,
    SAVE_GROUP_POST,
    SAVE_GROUP_COMMENT,
    EDIT_GROUP_COMMENT,
    EDIT_GROUP_COMMENT_START,
    EDIT_GROUP_COMMENT_SUCCESS,
    EDIT_GROUP_COMMENT_FAILED,
    UPDATE_GROUP_COMMENT_LIKE,
    UPDATE_GROUP_CONVERSATION_LIKE,
    GROUP_PAGE_JOIN_GROUP,
    GROUP_PAGE_JOIN_GROUP_SUCCESS,
    GROUP_PAGE_REQUEST_JOIN_GROUP,
    GROUP_PAGE_CANCEL_REQUEST_JOIN_GROUP,
    GROUP_PAGE_ACCEPT_INVITATION,
    GROUP_PAGE_ACCEPT_INVITATION_SUCCESS,
    REMOVE_GROUP_MEMBER,
    REMOVE_GROUP_MEMBER_SUCCESS,
    REMOVE_GROUP_MEMBER_FAILED,
    UPDATE_GROUP_MEMBER_ROLE,
    UPDATE_GROUP_MEMBER_ROLE_SUCCESS,
    UPDATE_GROUP_MEMBER_ROLE_FAILED,
    TRANSFER_GROUP_OWNERSHIP,
    TRANSFER_GROUP_OWNERSHIP_SUCCESS,
    TRANSFER_GROUP_OWNERSHIP_FAILED,
    FETCH_GROUP_CHALLENGES,
    FETCH_GROUP_CHALLENGES_START,
    FETCH_GROUP_CHALLENGES_SUCCESS,
    FETCH_GROUP_CHALLENGES_FAILED,
    UPDATE_GROUP_CHALLENGE,
    UPDATE_GROUP_CHALLENGE_START,
    UPDATE_GROUP_CHALLENGE_SUCCESS,
    UPDATE_GROUP_CHALLENGE_FAILED,
    DELETE_GROUP_CHALLENGE,
    DELETE_GROUP_CHALLENGE_START,
    DELETE_GROUP_CHALLENGE_SUCCESS,
    DELETE_GROUP_CHALLENGE_FAILED,
    UPLOAD_POST_IMAGE,
    UPLOAD_POST_IMAGE_START,
    UPLOAD_POST_IMAGE_SUCCESS,
    UPLOAD_POST_IMAGE_FAILED,
    DELETE_POST_IMAGE,
    DELETE_POST_IMAGE_SUCCESS,
    DELETE_POST_IMAGE_FAILED,
    UPDATE_GROUP_USER_CONNECTION,
    UPDATE_GROUP_USER_CONNECTION_DONE,
    INITIALIZE_GROUP_SINGLE_PAGE,
    INITIALIZE_GROUP_SINGLE_PAGE_SUCCESS,
    INITIALIZE_GROUP_SINGLE_PAGE_FAILED,
    SET_GROUP_PAGE_FEED_TAB,
    SET_GROUP_PAGE_FEED_TAB_START
} from '../actions/ActionTypes';

import { setPageStatus } from 'actions/index';

import {
    fetchGroupFail,
    fetchGroupPostsFailed,
    fetchGroupPostsSuccess,
    fetchGroupSuccess,
    saveGroupAnnouncementFailed,
    saveGroupAnnouncementSuccess,
    saveGroupPostFailed,
    saveGroupPostSuccess,
    fetchGroupCommentsSuccess,
    fetchGroupCommentsFailed,
    saveGroupCommentFailed,
    saveGroupCommentSuccess,
    deleteGroupPostSuccess,
    deleteGroupPostFailed,
    deleteGroupPost,
    deleteGroupAnnouncement,
    deleteGroupAnnouncementSuccess,
    deleteGroupAnnouncementFailed,
    closeDeleteConfirmDialog,
    deleteGroupCommentFailed,
    deleteGroupCommentSuccess,
    fetchGroupConversationLikersDone,
    fetchGroupCommentLikersDone,
    updateGroupConversationLikeStart,
    updateGroupConversationLikeDone,
    updateGroupCommentLikeStart,
    updateGroupCommentLikeDone,
    fetchGroupActivityFeedsFailed,
    fetchGroupActivityFeedsSuccess,
    updateGroupMemberRequest,
    initCourseTab,
    deleteGroupCourse,
    resetGroupFeedAnimateState,
    confirmLeaveGroup,
    setGroupPageTab,
    uploadPostImage,
    deletePostImage,
    sortGroupMembers,
    fetchGroupPost,
    fetchGroupActivityFeed,
    updateGroupPageShowErrorAlert
} from '../actions/groupPageActions';
import {
    selectDeleteDialogState,
    selectGroup,
    selectGroupAllFeedsCursor,
    selectGroupPost,
    selectGroupActivityFeedsCursor,
    selectGroupAnnouncement,
    selectGroupId,
    selectGroupMembers,
    selectGroupCommentsNextCursor,
    selectGroupPostsCursor,
    selectGroupConversationLikeUpdating,
    selectGroupCommentLikeUpdaing,
    selectViewerSocialProfile,
    selectViewerUserPreferences,
    selectGroupMemberRequest,
    selectGroupInvitation
} from '../reducers/group/groupPage';
import { setSocialLimitErrorDialog } from 'actions/socialActions';
import '../actions/groupPageActionsTypeDefs';
import {
    fetchMultipleUserSocialProfileImage,
    fetchMultipleUserSocialProfileImageDone
} from 'actions/userSocialProfileImageActions';

export function* fetchGroupPostsSaga() {
    const groupId = yield select(selectGroupId);
    const cursor = yield select(selectGroupPostsCursor);

    const payload = {
        limit: 10
    };
    if (cursor) {
        payload.cursor = cursor;
    }
    try {
        const response = yield call(gcApi.get, `/conversation-service/newspost/${groupId}/GROUP/feed`, {
            params: payload
        });
        const { newsPosts, nextCursor } = response.data;

        yield put(
            fetchGroupPostsSuccess({
                posts: newsPosts || [],
                hasMore: !!nextCursor,
                cursor: nextCursor
            })
        );

        yield fork(fetchPostsProfileImageAndFrameSaga, response.data);
    } catch (e) {
        yield put(fetchGroupPostsFailed());
    }
}

export function* fetchGroupPostSaga({ groupId, postUuid }) {
    try {
        yield put({ type: FETCH_GROUP_POST_START });
        const response = yield call(gcApi.get, `/conversation-service/newspost/${groupId}/GROUP/feed/${postUuid}`);

        yield put({
            type: FETCH_GROUP_POST_SUCCESS,
            post: response.data
        });

        yield fork(fetchPostProfileImageAndFrameSaga, response.data);
    } catch (e) {
        yield put({ type: FETCH_GROUP_POST_FAILED, errorStatus: e && e.response && e.response.status });
    }
}

export function* fetchGroupMemberRequest({ groupId, displayName }) {
    try {
        const response = yield call(gcApi.get, `/group-service/group/${groupId}/request/displayName/${displayName}`);
        return response;
    } catch (e) {
        return null;
    }
}

// worker
export function* fetchGroupSaga({ groupId, viewerSocialProfile }) {
    try {
        let callList = [
            call(gcApi.get, `/group-service/group/${groupId}?includeMembership=true`),
            call(gcApi.get, `/group-service/group/${groupId}/members?supportOwner=true`)
        ];
        if (viewerSocialProfile && viewerSocialProfile.profileId && viewerSocialProfile.displayName) {
            const userProfileId = viewerSocialProfile.profileId;
            const displayName = viewerSocialProfile.displayName;
            callList = callList.concat([
                /** group user connections */
                call(
                    gcApi.get,
                    `/group-service/group/${groupId}/member/${userProfileId}/connections?supportOwner=true`
                ),
                /** group member request */
                call(fetchGroupMemberRequest, { groupId, displayName }),
                /** group unhandled requests, both join-group requests and invitations */
                call(gcApi.get, `/group-service/requestNotHandled/${displayName}`)
            ]);
        }

        const [
            groupRes,
            groupMembersRes,
            groupUserConnectionsRes,
            groupMemberRequestRes,
            groupUnhandledRequestsRes
        ] = yield all(callList);

        /** if viewer is group member */
        const membershipStatus = (groupRes.data && groupRes.data.membershipStatus) || '';
        const isGroupMember = [GROUP_ROLE.OWNER, GROUP_ROLE.ADMIN, GROUP_ROLE.MEMBER].includes(membershipStatus);

        let groupAnnouncementRes;
        if (isGroupMember) {
            groupAnnouncementRes = yield call(gcApi.get, `/group-service/group/${groupId}/announcement`);
        }

        /**
         * @type {TFetchGroupSuccessPayload}
         */
        let payload = {
            group: groupRes.data,
            groupMembers: groupMembersRes.data
        };

        if (groupAnnouncementRes) {
            payload.groupAnnouncement = groupAnnouncementRes.data;
        }

        if (groupUserConnectionsRes) {
            payload.groupUserConnections = groupUserConnectionsRes.data;
        }
        if (groupMemberRequestRes && groupMemberRequestRes.data && typeof groupMemberRequestRes.data === 'object') {
            payload.groupMemberRequest = groupMemberRequestRes.data;
        }
        if (
            groupUnhandledRequestsRes &&
            groupUnhandledRequestsRes.data &&
            Array.isArray(groupUnhandledRequestsRes.data)
        ) {
            payload.groupInvitation = groupUnhandledRequestsRes.data.find(
                (invitation) => invitation.groupId === parseInt(groupId)
            );
        }

        yield put(fetchGroupSuccess(payload));
        yield put(sortGroupMembers());

        if (isGroupMember) {
            if (groupRes.data.featureTypes.includes('COURSES')) {
                yield put(initCourseTab());
            }

            yield put(fetchRouteDifficulties());
            /** leaderboard require activityTypes to work */
            const activityTypes = yield select(getActivityTypes);
            if (!activityTypes || activityTypes.length === 0) {
                yield put(fetchActivityTypes());
            }
        }

        yield fork(fetchGroupMemberProfileImageAndFrameSaga, groupMembersRes.data);
        yield fork(fetchGroupMemberProfileImageAndFrameSaga, groupMemberRequestRes?.data);
        yield fork(fetchGroupMemberProfileImageAndFrameSaga, groupUserConnectionsRes?.data);
        yield fork(fetchGroupMemberProfileImageAndFrameSaga, groupUnhandledRequestsRes?.data);
    } catch (e) {
        let isGroupPrivate = false;
        let isGroupDeleted = false;
        if (e.response && e.response.status === 403) {
            isGroupPrivate = true;
        } else if (e.response && e.response.status === 404) {
            isGroupDeleted = true;
        } else {
            yield put(setPageStatus(e.response.status));
        }
        yield put(fetchGroupFail({ isGroupPrivate, isGroupDeleted }));
    }
}

export function* saveGroupAnnouncementSaga({ text }) {
    const group = yield select(selectGroup);

    try {
        const payload = {
            groupId: group.id,
            message: text,
            title: '',
            announcementDate: new Date().toISOString()
        };
        const response = yield call(gcApi.post, `/group-service/group/${group.id}/announcement`, payload);

        pushGroupFeedEventsToAnalytics('Add Announcement');
        yield put(saveGroupAnnouncementSuccess({ groupAnnouncement: response.data }));
    } catch (error) {
        yield put(saveGroupAnnouncementFailed());
    }
}

export function* deleteGroupAnnouncementSaga() {
    const group = yield select(selectGroup);
    const groupAnnouncement = yield select(selectGroupAnnouncement);

    try {
        yield call(gcApi.delete, `/group-service/group/${group.id}/announcement/${groupAnnouncement.announcementId}`);
        yield put(resetGroupFeedAnimateState());

        pushGroupFeedEventsToAnalytics('Delete Announcement');
        yield put(deleteGroupAnnouncementSuccess());
    } catch (error) {
        yield put(deleteGroupAnnouncementFailed());
    }
}

export function* saveGroupPostSaga({ text }) {
    const groupId = yield select(selectGroupId);

    function* retryUpload() {
        const groupPostImages = yield select((state) => state.groupPage.groupPostImages);
        const images = groupPostImages.filter((image) => image.error);

        for (let index = 0; index < images.length; index++) {
            const image = images[index];
            if (image.error) {
                yield put(
                    uploadPostImage({
                        retryIndex: image.index
                    })
                );
                // Wait on process to be done, so we can ensure making the post only after all failed images are re-uploaded
                yield take([UPLOAD_POST_IMAGE_SUCCESS, UPLOAD_POST_IMAGE_FAILED]);
            }
        }
    }

    /** Retry all failed images first and don't handle error case */
    yield call(retryUpload);

    const groupPostImages = yield select((state) => state.groupPage.groupPostImages);
    const postImages = groupPostImages.map((image) => image.postImage);

    /** Post to server */
    try {
        const payload = {
            userGroupPk: groupId,
            newsPostType: 'GROUP',
            rulePk: 4
        };
        if (text.length > 0) {
            payload.body = text;
        }
        if (postImages.length > 0) {
            payload.postImages = postImages;
        }
        const response = yield call(gcApi.post, `/conversation-service/newspost/${groupId}/GROUP`, payload);

        pushGroupFeedEventsToAnalytics('Add a Post');

        yield put(saveGroupPostSuccess({ newPost: response.data }));
    } catch (error) {
        yield put(saveGroupPostFailed());
    }
}

export function* deleteGroupPostSaga({ postUuid }) {
    const groupId = yield select(selectGroupId);
    try {
        yield call(gcApi.delete, `/conversation-service/newspost/${groupId}/GROUP/${postUuid}`);
        yield put(resetGroupFeedAnimateState());

        pushGroupFeedEventsToAnalytics('Delete a Post');
        yield put(deleteGroupPostSuccess({ postUuid }));
    } catch (error) {
        yield put(deleteGroupPostFailed({ postUuid }));
    }
}

export function* fetchGroupCommentsSaga({ resourceType, resourceId }) {
    const groupId = yield select(selectGroupId);
    let start = yield select(selectGroupCommentsNextCursor, { resourceType, resourceId });

    try {
        const response = yield call(
            gcApi.get,
            `/conversation-service/conversation/comments/${groupId}/${resourceType}/${resourceId}`,
            {
                params: {
                    start: start,
                    limit: 10 + 1 /** fetch 1 more to improve pagination UX */
                }
            }
        );
        /** comments are reversed, so slice from back */
        const list = response.data.slice(-10);
        yield put(
            fetchGroupCommentsSuccess({
                resourceType,
                resourceId,
                comments: list,
                hasMore: response.data.length > 10
            })
        );
        yield fork(fetchGroupCommentsProfileImageAndFrameSaga, response.data);
    } catch (error) {
        yield put(fetchGroupCommentsFailed({ resourceType, resourceId }));
    }
}

export function* saveGroupCommentSaga({ resourceType, resourceId, text }) {
    try {
        const groupId = yield select(selectGroupId);
        const payload = {
            conversationUuid: null,
            conversationCommentUuid: null,
            body: text
        };
        const commentResponse = yield call(
            gcApi.post,
            `/conversation-service/conversation/comment/${groupId}/${resourceType}/${resourceId}/null`,
            payload
        );

        const newComment = commentResponse.data;

        // Add GA events
        if (resourceType === RESOURCE_TYPE.activity) {
            pushGroupFeedEventsToAnalytics('Add an Activity Comment');
        } else if (resourceType === RESOURCE_TYPE.post) {
            pushGroupFeedEventsToAnalytics('Add a Post Comment');
        }

        yield put(saveGroupCommentSuccess({ resourceType, resourceId, newComment }));
    } catch (e) {
        const errorReachLimit = e.response && e.response.status === 429;
        yield put(saveGroupCommentFailed({ resourceType, resourceId, errorReachLimit }));
        if (errorReachLimit) {
            yield put(setSocialLimitErrorDialog({ open: true, errorType: 'comment', isGroup: true }));
        }
    }
}

export function* deleteGroupCommentSaga({ resourceType, resourceId, comment }) {
    try {
        const { conversationUuid, conversationCommentUuid, conversationCommentPk } = comment;
        yield call(
            gcApi.delete,
            `/conversation-service/conversation/comment/${conversationUuid}/${conversationCommentUuid}/${conversationCommentPk}`
        );

        if (resourceType === RESOURCE_TYPE.activity) {
            pushGroupFeedEventsToAnalytics('Delete an Activity Comment');
        } else if (resourceType === RESOURCE_TYPE.post) {
            pushGroupFeedEventsToAnalytics('Delete a Post Comment');
        }
        yield put(deleteGroupCommentSuccess({ resourceType, resourceId, comment }));
    } catch (error) {
        yield put(deleteGroupCommentFailed({ resourceType, resourceId, comment }));
    }
}

export function* confirmDeleteDialogSaga() {
    const deleteDialogState = yield select(selectDeleteDialogState);
    switch (deleteDialogState.deleteType) {
        case 'post':
            yield put(deleteGroupPost({ postUuid: deleteDialogState.postUuid }));
            break;
        case 'announcement':
            yield put(deleteGroupAnnouncement());
            break;
        case 'course':
            yield put(deleteGroupCourse(deleteDialogState.course.courseId));
            break;
        case 'group':
            yield fork(deleteGroupSaga);
            break;
        case 'leave-group':
            yield put(confirmLeaveGroup());
            break;
        case 'postImage':
            yield put(deletePostImage(deleteDialogState.postImage));
            break;
        default:
            break;
    }
    yield put(closeDeleteConfirmDialog());
}

export function* fetchGroupConversationLikersSaga({ resourceType, resourceId }) {
    const groupId = yield select(selectGroupId);
    try {
        const response = yield call(
            gcApi.get,
            `/conversation-service/conversation/likes/${groupId}/${resourceType}/${resourceId}`,
            {
                params: {
                    connectionStatusIncluded: true
                }
            }
        );
        yield put(fetchGroupConversationLikersDone({ resourceType, resourceId, likers: response.data, error: false }));

        yield fork(fetchLikerProfileImageAndFrameSaga, response.data);
    } catch (error) {
        yield put(fetchGroupConversationLikersDone({ resourceType, resourceId, error: true }));
    }
}

export function* fetchGroupCommentLikersSaga({ resourceType, resourceId, conversationCommentUuid }) {
    try {
        const response = yield call(
            gcApi.get,
            `/conversation-service/conversation/comment/likes/${conversationCommentUuid}`
        );
        yield put(
            fetchGroupCommentLikersDone({
                resourceType,
                resourceId,
                conversationCommentUuid,
                likers: response.data,
                error: false
            })
        );
    } catch (error) {
        yield put(fetchGroupCommentLikersDone({ resourceType, resourceId, conversationCommentUuid, error: true }));
    }
}

export function* updateGroupConversationLikeSaga({ resourceType, resourceId, like, conversationUuid, likerInfo }) {
    // Prevent duplicated API calls before the previous one (like/unlike) is done
    const updating = yield select((state) => selectGroupConversationLikeUpdating(state, { resourceType, resourceId }));
    if (updating) return;

    try {
        yield put(
            updateGroupConversationLikeStart({
                resourceType,
                resourceId
            })
        );

        const groupId = yield select(selectGroupId);
        let response;
        if (like) {
            response = yield call(
                gcApi.post,
                `/conversation-service/conversation/like/${groupId}/${resourceType}/${resourceId}`
            );
        } else {
            yield call(gcApi.delete, `/conversation-service/conversation/like/${conversationUuid}/0`);
        }

        // Add GA events
        if (like) {
            if (resourceType === RESOURCE_TYPE.activity) {
                pushGroupFeedEventsToAnalytics('Like an Activity');
            } else if (resourceType === RESOURCE_TYPE.post) {
                pushGroupFeedEventsToAnalytics('Like a Post');
            }
        } else {
            if (resourceType === RESOURCE_TYPE.activity) {
                pushGroupFeedEventsToAnalytics('Unlike an Activity');
            } else if (resourceType === RESOURCE_TYPE.post) {
                pushGroupFeedEventsToAnalytics('Unlike a Post');
            }
        }

        if (like && !conversationUuid) {
            const conversationLikeDto = response.data;
            // update the conversationUuid back to activity/post feed
            yield put(
                updateGroupConversationLikeDone({
                    resourceType,
                    resourceId,
                    like,
                    likerInfo,
                    conversationUuid: response.data.conversationUuid,
                    conversationLikeDto
                })
            );
        } else {
            yield put(updateGroupConversationLikeDone({ resourceType, resourceId, like, likerInfo }));
        }
    } catch (error) {
        const errorReachLimit = error.response && error.response.status === 429;
        yield put(updateGroupConversationLikeDone({ resourceType, resourceId, like, error: true, errorReachLimit }));
        if (errorReachLimit) {
            yield put(setSocialLimitErrorDialog({ open: true, errorType: 'like', isGroup: true }));
        }
    }
}

export function* updateGroupCommentLikeSaga({ resourceType, resourceId, conversationCommentUuid, like }) {
    // Prevent duplicated API calls before the previous one (like/unlike) is done
    const updating = yield select((state) =>
        selectGroupCommentLikeUpdaing(state, { resourceType, resourceId, conversationCommentUuid })
    );
    if (updating) return;

    try {
        yield put(
            updateGroupCommentLikeStart({
                resourceType,
                resourceId,
                conversationCommentUuid
            })
        );

        let response;
        if (like) {
            response = yield call(
                gcApi.post,
                `/conversation-service/conversation/comment/like/${conversationCommentUuid}`
            );

            if (resourceType === RESOURCE_TYPE.activity) {
                pushGroupFeedEventsToAnalytics('Like an Activity Comment');
            } else if (resourceType === RESOURCE_TYPE.post) {
                pushGroupFeedEventsToAnalytics('Like a Post Comment');
            }
        } else {
            yield call(gcApi.delete, `/conversation-service/conversation/comment/like/${conversationCommentUuid}/0`);

            if (resourceType === RESOURCE_TYPE.activity) {
                pushGroupFeedEventsToAnalytics('Unlike an Activity Comment');
            } else if (resourceType === RESOURCE_TYPE.post) {
                pushGroupFeedEventsToAnalytics('Unlike a Post Comment');
            }
        }

        yield put(
            updateGroupCommentLikeDone({
                resourceType,
                resourceId,
                conversationCommentUuid,
                like,
                likeDto: (response && response.data) || null
            })
        );
    } catch (error) {
        const errorReachLimit = error.response && error.response.status === 429;
        yield put(
            updateGroupCommentLikeDone({
                resourceType,
                resourceId,
                conversationCommentUuid,
                like,
                error: true,
                errorReachLimit
            })
        );
        if (errorReachLimit) {
            yield put(setSocialLimitErrorDialog({ open: true, errorType: 'like', isGroup: true }));
        }
    }
}

export function* confirmLeaveGroupSaga() {
    const groupId = yield select(selectGroupId);
    const viewerSocialProfile = yield select(selectViewerSocialProfile);
    const { profileId } = viewerSocialProfile;
    try {
        yield call(gcApi.delete, `/group-service/group/${groupId}/member/${profileId}`);
        NavigationUtil.navigate('/groups/groupLeft');
    } catch (error) {}
}

export function* fetchGroupAllFeedsSaga() {
    try {
        yield put({ type: FETCH_GROUP_ALL_FEEDS_START });

        const groupId = yield select(selectGroupId);
        let url = `/web-gateway/group/v2/${groupId}/groupFeed`;
        const cursor = yield select(selectGroupAllFeedsCursor);

        const params = {};
        if (cursor) {
            params.cursor = cursor;
        }
        params['image-version'] = Object.values(PROFILE_IMAGE_VERSION);
        const querystring = toQueryString(params);

        url += querystring;
        const response = yield call(gcApi.get, url);

        const { groupFeed, nextCursor, daysLimit } = response.data;
        yield put({
            type: FETCH_GROUP_ALL_FEEDS_SUCCESS,
            list: groupFeed,
            hasMore: nextCursor !== null && nextCursor !== undefined,
            cursor: nextCursor,
            daysLimit
        });
        yield fork(fetchFeedProfileImageAndFrameSaga, response.data);
    } catch (e) {
        yield put({ type: FETCH_GROUP_ALL_FEEDS_FAILED });
    }
}

export function* fetchGroupActivityFeedsSaga() {
    try {
        const groupId = yield select(selectGroupId);
        let url = `/web-gateway/group/v2/${groupId}/activityFeed`;
        const cursor = yield select(selectGroupActivityFeedsCursor);

        const params = { limit: 10 };
        if (cursor) {
            params.cursor = cursor;
        }
        params['image-version'] = Object.values(PROFILE_IMAGE_VERSION);
        const querystring = toQueryString(params);

        url += querystring;

        const response = yield call(gcApi.get, url);
        let { activityList, filter, daysLimit } = response.data;

        yield put(
            fetchGroupActivityFeedsSuccess({
                list: activityList,
                hasMore: filter.nextCursor !== null && filter.nextCursor !== undefined,
                cursor: filter.nextCursor,
                daysLimit
            })
        );
        yield fork(fetchFeedProfileImageAndFrameSaga, response.data);
    } catch (e) {
        yield put(fetchGroupActivityFeedsFailed());
    }
}

export function* fetchGroupActivityFeedSaga({ groupId, activityId }) {
    try {
        yield put({ type: FETCH_GROUP_ACTIVITY_FEED_START });

        const response = yield call(gcApi.get, `/web-gateway/group/${groupId}/activityFeed/${activityId}`);

        yield put({
            type: FETCH_GROUP_ACTIVITY_FEED_SUCCESS,
            activity: response.data
        });
        yield fork(fetchFeedProfileImageAndFrameSaga, response.data);
    } catch (e) {
        yield put({ type: FETCH_GROUP_ACTIVITY_FEED_FAILED, errorStatus: e && e.response && e.response.status });
    }
}

export function* deleteGroupSaga() {
    const group = yield select(selectGroup);
    try {
        yield call(gcApi.delete, `/group-service/group/${group.id}`);
        window.location.href = NavigationUtil.url(`/groups`);
    } catch (error) {}
}

export function* joinGroupSaga() {
    const group = yield select(selectGroup);
    const groupId = yield select(selectGroupId);
    const viewerSocialProfile = yield select(selectViewerSocialProfile);
    const viewerUserPreferences = yield select(selectViewerUserPreferences);
    const userProfileId = viewerSocialProfile.profileId;
    try {
        const response = yield call(gcApi.post, `/group-service/group/${groupId}/member/${userProfileId}`, {
            groupId,
            userProfileId
        });

        const groupAnnouncementRes = yield call(gcApi.get, `/group-service/group/${groupId}/announcement`);

        yield put({
            type: GROUP_PAGE_JOIN_GROUP_SUCCESS,
            member: response.data,
            groupAnnouncement: groupAnnouncementRes.data
        });

        yield put(sortGroupMembers());
        if (group.featureTypes.includes('COURSES')) {
            yield put(initCourseTab());
        }

        yield put(fetchRouteDifficulties());
        /** leaderboard require activityTypes to work */
        const activityTypes = yield select(getActivityTypes);
        if (!activityTypes || activityTypes.length === 0) {
            yield put(fetchActivityTypes());
        }
        /** Go to Feed tab */
        yield put(setGroupPageTab(TAB_IDS.FEED));

        PrivacyAlertUtil.renderPrivacyAlert(viewerSocialProfile, viewerUserPreferences, 'group');
    } catch (error) {
        yield put(updateGroupPageShowErrorAlert({ showAlert: true }));
    }
}

export function* requestJoinGroupSaga() {
    const group = yield select(selectGroup);
    const viewerSocialProfile = yield select(selectViewerSocialProfile);
    const viewerPreferences = yield select(selectViewerUserPreferences);
    const userProfileId = viewerSocialProfile.profileId;
    try {
        const response = yield call(gcApi.post, `/group-service/private/group/${group.id}`, {
            userGroupId: group.id,
            requestorId: userProfileId
        });
        yield put(updateGroupMemberRequest(response.data));
        PrivacyAlertUtil.renderPrivacyAlert(viewerSocialProfile, viewerPreferences, 'group');
    } catch (error) {
        yield put(updateGroupPageShowErrorAlert({ showAlert: true }));
    }
}

export function* cancelRequestJoinGroupSaga() {
    const groupId = yield select(selectGroupId);
    const groupMemberRequest = yield select(selectGroupMemberRequest);
    try {
        yield call(gcApi.delete, `/group-service/private/group/${groupId}`, {
            data: {
                userGroupId: groupId,
                groupMemberRequestId: groupMemberRequest.groupMemberRequestId
            }
        });
        yield put(updateGroupMemberRequest(null));
    } catch (error) {
        yield put(updateGroupPageShowErrorAlert({ showAlert: true }));
    }
}

export function* acceptInvitationSaga() {
    const group = yield select(selectGroup);
    const groupId = yield select(selectGroupId);
    const groupInvitation = yield select(selectGroupInvitation);
    const viewerSocialProfile = yield select(selectViewerSocialProfile);
    const viewerUserPreferences = yield select(selectViewerUserPreferences);
    const userProfileId = viewerSocialProfile.profileId;
    try {
        const { groupInviteId } = groupInvitation;
        yield call(gcApi.put, `/group-service/invite/group/${groupId}`, {
            userGroupId: groupId,
            groupInviteId
        });

        const membersRes = yield call(gcApi.get, `/group-service/group/${groupId}/members?supportOwner=true`);
        const groupAnnouncementRes = yield call(gcApi.get, `/group-service/group/${groupId}/announcement`);

        const userConnection = {
            connectionRequestId: null,
            connectionRequestorId: null,
            connectionStatus: 0,
            userProfileId,
            groupRole: GROUP_ROLE.MEMBER
        };

        yield put({
            type: GROUP_PAGE_ACCEPT_INVITATION_SUCCESS,
            members: membersRes.data,
            groupAnnouncement: groupAnnouncementRes.data,
            userConnection
        });

        yield put(sortGroupMembers());
        if (group.featureTypes.includes('COURSES')) {
            yield put(initCourseTab());
        }

        yield put(fetchRouteDifficulties());
        /** leaderboard require activityTypes to work */
        const activityTypes = yield select(getActivityTypes);
        if (!activityTypes || activityTypes.length === 0) {
            yield put(fetchActivityTypes());
        }
        /** Go to Feed tab */
        yield put(setGroupPageTab(TAB_IDS.FEED));

        PrivacyAlertUtil.renderPrivacyAlert(viewerSocialProfile, viewerUserPreferences, 'group');
    } catch (error) {
        yield put(updateGroupPageShowErrorAlert({ showAlert: true }));
    }
}

export function* removeGroupMemberSaga(action) {
    const { userProfileId } = action;
    const groupId = yield select(selectGroupId);
    try {
        yield call(gcApi.delete, `/group-service/group/${groupId}/member/${userProfileId}`);
        yield put({
            type: REMOVE_GROUP_MEMBER_SUCCESS,
            userProfileId
        });
    } catch (error) {
        yield put({
            type: REMOVE_GROUP_MEMBER_FAILED
        });
    }
}

export function* updateGroupMemberRoleSaga(action) {
    const { userProfileId, groupRole } = action;
    const groupId = yield select(selectGroupId);
    const groupMembers = yield select(selectGroupMembers);
    const member = groupMembers.find((member) => member.userProfileId === userProfileId);
    member.groupRole = groupRole;
    try {
        yield call(gcApi.put, `/group-service/group/${groupId}/member/${userProfileId}`, member);
        yield put({
            type: UPDATE_GROUP_MEMBER_ROLE_SUCCESS,
            updatedMember: member
        });
        yield put(sortGroupMembers());
    } catch (error) {
        yield put({
            type: UPDATE_GROUP_MEMBER_ROLE_FAILED
        });
    }
}

export function* transferGroupOwnershipSaga(action) {
    const { userProfileId } = action;
    try {
        const group = yield select(selectGroup);
        const newGroup = {
            ...group,
            ownerId: userProfileId,
            membershipStatus: GROUP_ROLE.ADMIN
        };
        const response = yield call(gcApi.put, `/group-service/group/${group.id}`, newGroup);
        yield put({
            type: TRANSFER_GROUP_OWNERSHIP_SUCCESS,
            newGroup: response.data
        });
        yield put(sortGroupMembers());
    } catch (error) {
        yield put({
            type: TRANSFER_GROUP_OWNERSHIP_FAILED
        });
    }
}

export function* fetchGroupChallengesSaga(action) {
    try {
        const groupId = yield select(selectGroupId);
        yield put({
            type: FETCH_GROUP_CHALLENGES_START
        });

        const [responseNonCompleted, responseUserJoined] = yield all([
            call(gcApi.get, `/groupchallenge-service/group/${groupId}/groupchallenge/nonCompleted`),
            call(gcApi.get, `/groupchallenge-service/group/${groupId}/groupchallenge/userJoined`)
        ]);

        yield put({
            type: FETCH_GROUP_CHALLENGES_SUCCESS,
            nonCompleted: responseNonCompleted.data,
            userJoined: responseUserJoined.data
        });
    } catch (error) {
        yield put({
            type: FETCH_GROUP_CHALLENGES_FAILED
        });
    }
}

export function* updateGroupChallengeSaga({ challenge }) {
    try {
        const groupId = yield select(selectGroupId);

        yield put({
            type: UPDATE_GROUP_CHALLENGE_START
        });

        yield call(gcApi.put, `/groupchallenge-service/group/${groupId}/groupchallenge/${challenge.uuid}/`, challenge);

        yield put({
            type: UPDATE_GROUP_CHALLENGE_SUCCESS,
            newChallenge: challenge
        });
    } catch (error) {
        yield put({
            type: UPDATE_GROUP_CHALLENGE_FAILED
        });
    }
}

export function* deleteGroupChallengeSaga({ uuid }) {
    try {
        const groupId = yield select(selectGroupId);

        yield put({
            type: DELETE_GROUP_CHALLENGE_START
        });

        yield call(gcApi.delete, `/groupchallenge-service/group/${groupId}/groupchallenge/${uuid}/`);

        yield put({
            type: DELETE_GROUP_CHALLENGE_SUCCESS,
            uuid
        });
    } catch (error) {
        yield put({
            type: DELETE_GROUP_CHALLENGE_FAILED
        });
    }
}

export function* uploadPostImageSaga({ file, imageB64, retryIndex, callback }) {
    const isRetry = typeof retryIndex === 'number';
    let index;
    if (isRetry) {
        index = retryIndex;
    } else {
        const groupPostImageLastIndex = yield select((state) => state.groupPage.groupPostImageLastIndex);
        index = Number.isInteger(groupPostImageLastIndex) ? groupPostImageLastIndex + 1 : 0;
    }

    try {
        callback(index, 0);
        yield put({
            type: UPLOAD_POST_IMAGE_START,
            index,
            file,
            imageB64,
            isRetry
        });

        if (isRetry) {
            const groupPostImages = yield select((state) => state.groupPage.groupPostImages);
            const postImage = groupPostImages.filter((image) => image.index === index).pop();
            // reuse the previous uploaded image file
            file = postImage.file;
        }

        const formData = new FormData();
        formData.append('file', file);
        const response = yield call(gcApi.post, `/conversation-service/newspost/image`, formData, {
            onUploadProgress: (progressEvent) => {
                const { loaded, total } = progressEvent;
                const progress = (loaded / total) * 90;
                // Execute callback function to update component progress state for upload phase
                callback(index, progress);
            },
            onDownloadProgress: (progressEvent) => {
                const { loaded, total } = progressEvent;
                const progress = 90 + (loaded / total) * 10;
                // Execute callback function to update component progress state for download phase
                callback(index, progress);
            }
        });

        yield put({
            type: UPLOAD_POST_IMAGE_SUCCESS,
            postImage: response.data,
            index
        });
    } catch (error) {
        yield put({
            type: UPLOAD_POST_IMAGE_FAILED,
            index
        });
    }
}

export function* updateGroupUserConnectionSaga({ groupUserConnection }) {
    yield put({ type: UPDATE_GROUP_USER_CONNECTION_DONE, groupUserConnection });
    yield put(sortGroupMembers());
}

export function* initializeGroupSinglePageSaga({ groupId, activityId, postUuid, commentUuid, viewerSocialProfile }) {
    try {
        const response = yield call(gcApi.get, `/group-service/group/${groupId}?includeMembership=true`);

        yield put({
            type: INITIALIZE_GROUP_SINGLE_PAGE_SUCCESS,
            group: response.data,
            viewerSocialProfile,
            focusCommentUuid: commentUuid
        });

        if (activityId) {
            yield put(fetchGroupActivityFeed({ groupId, activityId }));
        } else if (postUuid) {
            yield put(fetchGroupPost({ groupId, postUuid }));
        }
    } catch (e) {
        yield put({
            type: INITIALIZE_GROUP_SINGLE_PAGE_FAILED
        });
    }
}

export function* setGroupPageFeedTabSaga({ feedTab }) {
    yield put({ type: SET_GROUP_PAGE_FEED_TAB_START, feedTab });
    yield put(resetGroupFeedAnimateState());
}

export function* deletePostImageSaga({ newsPostUuid, newsPostHasImageId }) {
    try {
        const post = yield select((state) => selectGroupPost(state, { postUuid: newsPostUuid }));
        yield call(gcApi.delete, `/conversation-service/newspost/${newsPostUuid}/image?imageId=${newsPostHasImageId}`);

        // if the image to be deleted is the only one in the post and the post has no text either,
        // we need to remove this post
        const { body, postImages } = post;
        const deletePost = !body && Array.isArray(postImages) && postImages.length === 1;
        if (deletePost) {
            yield put(deleteGroupPost({ postUuid: newsPostUuid }));
        }
        yield put({ type: DELETE_POST_IMAGE_SUCCESS, newsPostUuid, newsPostHasImageId, deletePost });
        // close the image slider modal
        yield put(hideModal());
    } catch (e) {
        yield put({ type: DELETE_POST_IMAGE_FAILED });
        yield put(hideModal());
    }
}

export function* editGroupCommentSaga({ resourceType, resourceId, data, conversationUuid, conversationCommentUuid }) {
    try {
        yield put({
            type: EDIT_GROUP_COMMENT_START,
            resourceType,
            resourceId,
            conversationCommentUuid
        });

        const response = yield call(
            gcApi.put,
            `/conversation-service/conversation/comment/${conversationUuid}/${conversationCommentUuid}`,
            data
        );

        yield put({
            type: EDIT_GROUP_COMMENT_SUCCESS,
            resourceType,
            resourceId,
            newComment: response.data,
            conversationCommentUuid
        });
    } catch (e) {
        yield put({
            type: EDIT_GROUP_COMMENT_FAILED,
            resourceType,
            resourceId,
            conversationCommentUuid
        });
    }
}

/// section: fetching profile image and frame

/**
 * handle fetch group member response
 * @param {Object[]} responseData
 * @param {number} responseData[].userProfileId
 */
function* fetchGroupMemberProfileImageAndFrameSaga(responseData) {
    if (!responseData?.length) {
        return;
    }
    const userIds = responseData.map((item) => item.userProfileId);
    yield fork(fetchProfileImageAndFrameSaga, userIds);
}

/**
 * handle fetch likers response. (use in opening liker dialog)
 * @param {{userProfilePk: number}[]} responseData
 */
function* fetchLikerProfileImageAndFrameSaga(responseData) {
    const userIds = (responseData || []).map((item) => item.userProfilePk);
    yield fork(fetchProfileImageAndFrameSaga, userIds);
}

/**
 * handle web-gateway response. profile images and frames are include in `responseData.profileInfos`
 * @param {Object} responseData
 * @param {Object} responseData.profileInfos
 * @returns
 */
function* fetchFeedProfileImageAndFrameSaga(responseData) {
    if (!responseData?.profileInfos) {
        return;
    }
    yield put(fetchMultipleUserSocialProfileImageDone({ data: responseData?.profileInfos }));
}

/**
 * handle fetch posts response. profile images and frames are fetched separately
 * @param {Object} responseData
 * @param {Object[]} [responseData.newsPosts]
 * @param {number} [responseData.newsPosts[].userProfilePk]
 * @param {Object} [responseData.newsPosts[].conversationLikersDTO]
 * @param {number[]} [responseData.newsPosts[].conversationLikersDTO.likerUserIds]
 * @returns
 */
function* fetchPostsProfileImageAndFrameSaga(responseData) {
    if (!Array.isArray(responseData?.newsPosts) || !responseData?.newsPosts?.length) {
        return;
    }
    let userIds = [];
    responseData.newsPosts.forEach((item) => {
        if (item?.userProfilePk) {
            userIds.push(item.userProfilePk);
        }
        if (Array.isArray(item?.conversationLikersDTO?.likerUserIds)) {
            userIds = userIds.concat(item.conversationLikersDTO.likerUserIds);
        }
    });
    yield fork(fetchProfileImageAndFrameSaga, userIds);
}

/**
 * handle fetch single post response. profile images and frames are fetched separately
 * @param {Object} responseData
 * @param {number} responseData.userProfilePk
 * @param {Object} [responseData.conversationLikersDTO]
 * @param {number[]} [responseData.conversationLikersDTO.likerUserIds]
 * @returns
 */
function* fetchPostProfileImageAndFrameSaga(responseData) {
    if (!responseData?.userProfilePk) {
        return;
    }
    let userIds = [responseData.userProfilePk];
    if (Array.isArray(responseData.conversationLikersDTO?.likerUserIds)) {
        userIds = userIds.concat(responseData.conversationLikersDTO.likerUserIds);
    }
    yield fork(fetchProfileImageAndFrameSaga, userIds);
}

/**
 * handle comments profile images and frames
 * @param {Object[]} responseData
 * @param {number} responseData[].userProfilePk
 */
function* fetchGroupCommentsProfileImageAndFrameSaga(responseData) {
    if (!responseData?.length) {
        return;
    }
    const userIds = responseData.map((item) => item.userProfilePk);
    yield fork(fetchProfileImageAndFrameSaga, userIds);
}

function* fetchProfileImageAndFrameSaga(userIds) {
    if (!userIds || !userIds.length) {
        return;
    }
    yield put(fetchMultipleUserSocialProfileImage(userIds));
}

/// end section: fetching profile image and frame

// watcher
export const groupPageSagas = [
    /** Group Initial Fetches - Group Detail Page */
    takeLatest(FETCH_GROUP, fetchGroupSaga),
    /** Group Initial Fetches - Group Single Page */
    takeLatest(INITIALIZE_GROUP_SINGLE_PAGE, initializeGroupSinglePageSaga),
    /** Group All Feeds */
    takeLatest(FETCH_GROUP_ALL_FEEDS, fetchGroupAllFeedsSaga),
    /** Group Activity Feeds */
    takeLatest(FETCH_GROUP_ACTIVITY_FEEDS, fetchGroupActivityFeedsSaga),
    takeLatest(FETCH_GROUP_ACTIVITY_FEED, fetchGroupActivityFeedSaga),
    /** Group Announcement */
    takeLatest(SAVE_GROUP_ANNOUNCEMENT, saveGroupAnnouncementSaga),
    takeLatest(DELETE_GROUP_ANNOUNCEMENT, deleteGroupAnnouncementSaga),
    /** Group Posts (Comments) */
    takeLatest(SAVE_GROUP_POST, saveGroupPostSaga),
    takeLatest(DELETE_GROUP_POST, deleteGroupPostSaga),
    takeLatest(FETCH_GROUP_POSTS, fetchGroupPostsSaga),
    takeLatest(FETCH_GROUP_POST, fetchGroupPostSaga),
    takeLatest(FETCH_GROUP_COMMENTS, fetchGroupCommentsSaga),
    takeLatest(SAVE_GROUP_COMMENT, saveGroupCommentSaga),
    takeLatest(DELETE_GROUP_COMMENT, deleteGroupCommentSaga),
    takeLatest(EDIT_GROUP_COMMENT, editGroupCommentSaga),
    /** Group Posts (Images) */
    takeEvery(UPLOAD_POST_IMAGE, uploadPostImageSaga),
    takeEvery(DELETE_POST_IMAGE, deletePostImageSaga),
    /** Activity / Post (Comment) Likes */
    takeEvery(FETCH_GROUP_CONVERSATION_LIKERS, fetchGroupConversationLikersSaga),
    takeEvery(FETCH_GROUP_COMMENT_LIKERS, fetchGroupCommentLikersSaga),
    takeEvery(UPDATE_GROUP_CONVERSATION_LIKE, updateGroupConversationLikeSaga),
    takeEvery(UPDATE_GROUP_COMMENT_LIKE, updateGroupCommentLikeSaga),
    /** Delete Group / Post / Couse */
    takeLatest(CONFIRM_DELETE_CONFIRM_DIALOG, confirmDeleteDialogSaga),
    /** Join / Request Join / Cancel Request Join / Accept Invitation / Leave a group */
    takeLatest(CONFIRM_LEAVE_GROUP, confirmLeaveGroupSaga),
    takeLatest(GROUP_PAGE_JOIN_GROUP, joinGroupSaga),
    takeLatest(GROUP_PAGE_REQUEST_JOIN_GROUP, requestJoinGroupSaga),
    takeLatest(GROUP_PAGE_CANCEL_REQUEST_JOIN_GROUP, cancelRequestJoinGroupSaga),
    takeLatest(GROUP_PAGE_ACCEPT_INVITATION, acceptInvitationSaga),
    /** Group Membership */
    takeLatest(REMOVE_GROUP_MEMBER, removeGroupMemberSaga),
    takeLatest(UPDATE_GROUP_MEMBER_ROLE, updateGroupMemberRoleSaga),
    takeLatest(TRANSFER_GROUP_OWNERSHIP, transferGroupOwnershipSaga),
    /** Group Challenges */
    takeLatest(FETCH_GROUP_CHALLENGES, fetchGroupChallengesSaga),
    takeLatest(UPDATE_GROUP_CHALLENGE, updateGroupChallengeSaga),
    takeLatest(DELETE_GROUP_CHALLENGE, deleteGroupChallengeSaga),
    /** Update Connection Status */
    takeEvery(UPDATE_GROUP_USER_CONNECTION, updateGroupUserConnectionSaga),
    /** Switch Group Feed Tab */
    takeEvery(SET_GROUP_PAGE_FEED_TAB, setGroupPageFeedTabSaga)
];
